import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ClinicianAiSummaryDto,
  createClinicianAiSummaryApi,
} from "../api-helpers/ClinicianAiSummaryApiHelper";

export type ClinicianAiSummaryHookStatus =
  | "idle"
  | "loading"
  | "empty"
  | "pending"
  | "processing"
  | "completed"
  | "error";

interface UseClinicianAiSummaryResult {
  status: ClinicianAiSummaryHookStatus;
  summary: ClinicianAiSummaryDto | null;
  errorMessage: string | null;
  regenerate: () => Promise<void>;
}

export function useClinicianAiSummary(
  patientId: string | undefined
): UseClinicianAiSummaryResult {
  const api = useMemo(() => createClinicianAiSummaryApi(), []);
  const [status, setStatus] = useState<ClinicianAiSummaryHookStatus>("idle");
  const [summary, setSummary] = useState<ClinicianAiSummaryDto | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const regenerate = useCallback(async () => {
    if (!patientId) return;
    setStatus("loading");
    setErrorMessage(null);
    try {
      const { data } = await api.generateSummary(patientId);
      setSummary(data);
      setStatus(data.status === "failed" ? "error" : data.status);
      if (data.status === "failed") {
        setErrorMessage(data.errorMessage);
      }
    } catch {
      setStatus("error");
      setErrorMessage("Failed to start summary generation.");
    }
  }, [api, patientId]);

  // Initial fetch on mount / patientId change.
  useEffect(() => {
    if (!patientId) return;

    let cancelled = false;

    const fetchInitial = async () => {
      setStatus("loading");
      setErrorMessage(null);
      try {
        const { data } = await api.getSummary(patientId);
        if (cancelled) return;
        if (!data) {
          // No summary exists yet: wait for the user to trigger generation manually
          // rather than auto-firing it (revised decision - see conversation history).
          setStatus("empty");
          return;
        }
        setSummary(data);
        if (data.status === "failed") {
          setErrorMessage(data.errorMessage);
          setStatus("error");
        } else {
          setStatus(data.status);
        }
      } catch {
        if (!cancelled) {
          setStatus("error");
          setErrorMessage("Unable to load the pre-visit summary.");
        }
      }
    };

    fetchInitial();

    return () => {
      cancelled = true;
    };
  }, [patientId]);

  return { status, summary, errorMessage, regenerate };
}
